<template>
  <div class="mobile-package-center" :class="{ 'dark-theme': isDark }">
    <!-- 顶部导航 -->
    <header class="mobile-header">
      <button @click="goBack" class="back-btn">
        <i class="fas fa-arrow-left"></i>
      </button>
      <h1>套餐中心</h1>
      <!-- 主题切换按钮 -->
      <div class="theme-toggle" @click="toggleTheme">
        <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
      </div>
    </header>

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 标签切换 -->
      <div class="tab-switcher">
        <button 
          @click="activeTab = 'membership'"
          :class="{ active: activeTab === 'membership' }"
          class="tab-btn"
        >
          <i class="fas fa-crown"></i>
          会员套餐
        </button>
        <button 
          @click="activeTab = 'package'"
          :class="{ active: activeTab === 'package' }"
          class="tab-btn"
        >
          <i class="fas fa-battery-three-quarters"></i>
          加量包
        </button>
      </div>

      <!-- 会员套餐 -->
      <div v-if="activeTab === 'membership'" class="tab-content">
        <div class="section-header">
          <h2>选择会员套餐</h2>
          <p>享受更多权益，体验更优质的AI视频生成服务</p>
        </div>

        <div v-if="loadingMemberships" class="loading-skeleton">
          <div v-for="i in 3" :key="i" class="skeleton-card">
            <div class="skeleton-header"></div>
            <div class="skeleton-body"></div>
            <div class="skeleton-footer"></div>
          </div>
        </div>

        <div v-else class="memberships-list">
          <div 
            v-for="membership in membershipList" 
            :key="membership.id"
            :class="[
              'membership-card',
              { 'featured': membership.type === 'vip' }
            ]"
          >
            <!-- 推荐标签 -->
            <div v-if="membership.type === 'vip'" class="featured-badge">
              <i class="fas fa-star"></i>
              推荐
            </div>

            <div class="membership-header">
              <div class="membership-icon">
                <i 
                  :class="[
                    membership.type === 'basic' ? 'fas fa-user' :
                    membership.type === 'vip' ? 'fas fa-crown' :
                    membership.type === 'svip' ? 'fas fa-gem' :
                    'fas fa-star'
                  ]"
                ></i>
              </div>
              <h3>{{ membership.name }}</h3>
              <p>{{ membership.description }}</p>
            </div>

            <div class="membership-price">
              <div class="price-main">
                ¥{{ parseFloat(membership.price).toFixed(0) }}
              </div>
              <div class="price-period">{{ membership.duration_days }}天有效期</div>
            </div>

            <div class="membership-features">
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>每日 {{ membership.daily_api_calls }} 次调用</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>{{ membership.duration_days }} 天有效期</span>
              </div>
              <div class="feature-item">
                <i class="fas fa-check"></i>
                <span>24/7 在线支持</span>
              </div>
              <div v-if="membership.type === 'vip' || membership.type === 'svip'" class="feature-item">
                <i class="fas fa-check"></i>
                <span>优先处理队列</span>
              </div>
              <div v-if="membership.type === 'svip'" class="feature-item">
                <i class="fas fa-check"></i>
                <span>专属客服支持</span>
              </div>
            </div>

            <button 
              @click="purchaseMembership(membership)"
              :disabled="purchasing"
              class="purchase-btn"
            >
              <i v-if="purchasing" class="fas fa-spinner fa-spin"></i>
              <span>{{ purchasing ? '购买中...' : '立即购买' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 加量包 -->
      <div v-if="activeTab === 'package'" class="tab-content">
        <div class="section-header">
          <h2>选择加量包</h2>
          <p>灵活购买调用次数，按需使用更经济</p>
        </div>

        <div v-if="loadingPackages" class="loading-skeleton">
          <div v-for="i in 4" :key="i" class="skeleton-card">
            <div class="skeleton-header"></div>
            <div class="skeleton-body"></div>
            <div class="skeleton-footer"></div>
          </div>
        </div>

        <div v-else class="packages-grid">
          <div 
            v-for="pkg in packageList" 
            :key="pkg.id"
            class="package-card"
          >
            <div class="package-header">
              <div class="package-icon">
                <i 
                  :class="[
                    pkg.type === 'basic' ? 'fas fa-battery-quarter' :
                    pkg.type === 'standard' ? 'fas fa-battery-half' :
                    pkg.type === 'premium' ? 'fas fa-battery-three-quarters' :
                    'fas fa-battery-full'
                  ]"
                ></i>
              </div>
              <h3>{{ pkg.name }}</h3>
              <p>{{ pkg.description }}</p>
            </div>

            <div class="package-amount">
              <div class="amount-main">
                {{ pkg.api_calls_increment.toLocaleString() }}
              </div>
              <div class="amount-unit">调用次数</div>
            </div>

            <div class="package-price">
              <div class="price-main">
                ¥{{ parseFloat(pkg.price).toFixed(0) }}
              </div>
              <div class="price-per-thousand">
                {{ (parseFloat(pkg.price) / pkg.api_calls_increment * 1000).toFixed(2) }}元/千次
              </div>
            </div>

            <button 
              @click="purchasePackage(pkg)"
              :disabled="purchasing"
              class="purchase-btn"
            >
              <i v-if="purchasing" class="fas fa-spinner fa-spin"></i>
              <span>{{ purchasing ? '购买中...' : '立即购买' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 购买历史 -->
      <div class="purchase-history">
        <div class="section-header">
          <h3>
            <i class="fas fa-history"></i>
            购买历史
          </h3>
        </div>
        
        <div v-if="loadingHistory" class="loading-skeleton">
          <div v-for="i in 3" :key="i" class="skeleton-history-item"></div>
        </div>
        
        <div v-else-if="purchaseHistory.length > 0" class="history-list">
          <div v-for="item in purchaseHistory" :key="item.id" class="history-item">
            <div class="history-info">
              <div class="history-name">
                <i :class="item.type === 'membership' ? 'fas fa-crown' : 'fas fa-battery-three-quarters'"></i>
                {{ item.name }}
              </div>
              <div class="history-date">{{ formatDate(item.created_at) }}</div>
            </div>
            <div class="history-details">
              <div class="history-price">¥{{ parseFloat(item.price || '0').toFixed(0) }}</div>
              <div class="history-expiry">{{ formatDate(item.expires_at) }} 到期</div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-history">
          <div class="empty-icon">
            <i class="fas fa-shopping-cart"></i>
          </div>
          <p>暂无购买记录</p>
        </div>
      </div>
    </main>

    <!-- 购买确认弹窗 -->
    <div v-if="showPurchaseConfirm" class="modal-overlay" @click="closePurchaseConfirm">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>确认购买</h3>
          <button @click="closePurchaseConfirm" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="selectedItem" class="purchase-details">
            <div class="item-info">
              <div class="item-icon">
                <i :class="selectedItem.type === 'membership' ? 'fas fa-crown' : 'fas fa-battery-three-quarters'"></i>
              </div>
              <div class="item-details">
                <h4>{{ selectedItem.name }}</h4>
                <p>{{ selectedItem.description }}</p>
              </div>
            </div>
            <div class="purchase-summary">
              <div class="summary-row">
                <span>商品名称:</span>
                <span>{{ selectedItem.name }}</span>
              </div>
              <div class="summary-row">
                <span>价格:</span>
                <span class="price">¥{{ parseFloat(selectedItem.price).toFixed(0) }}</span>
              </div>
              <div v-if="selectedItem.type === 'membership'" class="summary-row">
                <span>有效期:</span>
                <span>{{ selectedItem.duration_days }} 天</span>
              </div>
              <div v-if="selectedItem.type === 'package'" class="summary-row">
                <span>调用次数:</span>
                <span>{{ selectedItem.api_calls_increment.toLocaleString() }} 次</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closePurchaseConfirm" class="cancel-btn">取消</button>
          <button @click="confirmPurchase" :disabled="purchasing" class="confirm-btn">
            <i v-if="purchasing" class="fas fa-spinner fa-spin"></i>
            <span>{{ purchasing ? '购买中...' : '确认购买' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from '@/utils/router.js';
import { useUserStoreHook } from '@/store/modules/user';
import ActionAPI from '@/api/comfyui-action';
import type { ActionMembership, ActionPackage } from '@/api/comfyui-action/types';
import { ElMessage } from '@/utils/message.js';
import { useTheme } from '@/composables/useTheme';

const router = useRouter();
const userStore = useUserStoreHook();

// 主题管理
const { isDark, loadTheme, toggleTheme } = useTheme();

// 状态管理
const activeTab = ref<'membership' | 'package'>('membership');
const membershipList = ref<ActionMembership[]>([]);
const packageList = ref<ActionPackage[]>([]);
const purchaseHistory = ref<any[]>([]);
const loadingMemberships = ref(false);
const loadingPackages = ref(false);
const loadingHistory = ref(false);
const purchasing = ref(false);
const showPurchaseConfirm = ref(false);
const selectedItem = ref<any>(null);

// 获取会员套餐列表
const fetchMemberships = async () => {
  try {
    loadingMemberships.value = true;
    const response = await ActionAPI.getMemberships('active');
    if (response.success && response.data.memberships) {
      // 过滤掉 type 为 "free" 的会员套餐
      membershipList.value = response.data.memberships.filter(
        (membership: ActionMembership) => membership.type !== 'free'
      );
    }
  } catch (error) {
    console.error('获取会员套餐失败:', error);
    ElMessage.error('获取会员套餐失败');
  } finally {
    loadingMemberships.value = false;
  }
};

// 获取加量包列表
const fetchPackages = async () => {
  try {
    loadingPackages.value = true;
    const response = await ActionAPI.getPackages('active');
    if (response.success && response.data.packages) {
      packageList.value = response.data.packages;
    }
  } catch (error) {
    console.error('获取加量包失败:', error);
    ElMessage.error('获取加量包失败');
  } finally {
    loadingPackages.value = false;
  }
};

// 获取购买历史
const fetchPurchaseHistory = async () => {
  try {
    loadingHistory.value = true;
    const userId = getUserId();
    if (!userId) return;

    // 获取会员购买历史
    const membershipResponse = await ActionAPI.getUserMemberships(userId, 'active');
    // 获取加量包购买历史  
    const packageResponse = await ActionAPI.getUserPackages(userId, 'available');
    
    const history = [];
    
    if (membershipResponse.success && membershipResponse.data.user_memberships) {
      history.push(...membershipResponse.data.user_memberships.map((item: any) => ({
        ...item,
        name: item.membership_name,
        type: 'membership'
      })));
    }
    
    if (packageResponse.success && packageResponse.data.user_packages) {
      history.push(...packageResponse.data.user_packages.map((item: any) => ({
        ...item,
        name: item.package_name,
        type: 'package'
      })));
    }
    
    // 按创建时间排序
    purchaseHistory.value = history.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  } catch (error) {
    console.error('获取购买历史失败:', error);
  } finally {
    loadingHistory.value = false;
  }
};

// 购买会员套餐
const purchaseMembership = async (membership: ActionMembership) => {
  selectedItem.value = { ...membership, type: 'membership' };
  showPurchaseConfirm.value = true;
};

// 购买加量包
const purchasePackage = async (pkg: ActionPackage) => {
  selectedItem.value = { ...pkg, type: 'package' };
  showPurchaseConfirm.value = true;
};

// 确认购买
const confirmPurchase = async () => {
  if (!selectedItem.value) return;
  
  try {
    purchasing.value = true;
    const userId = getUserId();
    if (!userId) {
      ElMessage.warning('请先登录');
      return;
    }

    let response;
    if (selectedItem.value.type === 'membership') {
      response = await ActionAPI.purchaseMembership(userId, selectedItem.value.id);
    } else {
      const remainingCalls = selectedItem.value.api_calls_increment || selectedItem.value.quota || 0;
      const expiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      response = await ActionAPI.purchasePackage(userId, selectedItem.value.id, remainingCalls, expiresAt);
    }

    if (response.success) {
      ElMessage.success('购买成功！');
      closePurchaseConfirm();
      // 刷新购买历史
      fetchPurchaseHistory();
    }
  } catch (error: any) {
    console.error('购买失败:', error);
    ElMessage.error(error.response?.data?.message || '购买失败');
  } finally {
    purchasing.value = false;
  }
};

// 关闭购买确认弹窗
const closePurchaseConfirm = () => {
  showPurchaseConfirm.value = false;
  selectedItem.value = null;
};

// 格式化日期
const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('zh-CN');
};

// 获取用户ID
const getUserId = (): number | null => {
  const userId = userStore.userInfo.id;
  return userId ? Number(userId) : null;
};

// 返回上一页
const goBack = () => {
  router.back();
};

// 页面加载
onMounted(async () => {
  // 加载主题设置
  loadTheme();
  
  // 等待用户状态初始化完成
  await new Promise<void>(resolve => {
    const checkUserState = () => {
      if (userStore.token || localStorage.getItem('token')) {
        resolve();
      } else {
        // 等待50ms后再次检查，最多等待2秒
        setTimeout(() => {
          if (userStore.token || localStorage.getItem('token')) {
            resolve();
          } else if (Date.now() - startTime > 2000) {
            resolve(); // 超时也继续执行
          } else {
            checkUserState();
          }
        }, 50);
      }
    };
    const startTime = Date.now();
    checkUserState();
  });
  
  if (!userStore.token) {
    ElMessage.warning('请先登录');
    router.push('/pages/mobile/login?redirect=' + encodeURIComponent(router.currentRoute.value.fullPath));
    return;
  }
  
  fetchMemberships();
  fetchPackages();
  fetchPurchaseHistory();
});
</script>

<style scoped lang="scss">
.mobile-package-center {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-attachment: fixed; /* 确保背景固定 */
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 为了确保背景色覆盖整个页面高度 */
.mobile-package-center::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: -1;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 100;
  
  .back-btn {
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    color: white;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  
  h1 {
    color: white;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
  
  .header-placeholder {
    width: 40px;
  }
}

.main-content {
  flex: 1;
  padding: 20px 16px;
  position: relative;
  z-index: 1; /* 确保内容在背景之上 */
}

.tab-switcher {
  display: flex;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 24px;
  backdrop-filter: blur(10px);
  
  .tab-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &.active {
      background: rgba(255, 255, 255, 0.9);
      color: #333;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    i {
      font-size: 18px;
    }
  }
}

.tab-content {
  .section-header {
    text-align: center;
    margin-bottom: 24px;
    
    h2, h3 {
      color: white;
      font-size: 24px;
      font-weight: bold;
      margin: 0 0 8px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    
    h3 {
      font-size: 20px;
    }
    
    p {
      color: rgba(255, 255, 255, 0.8);
      font-size: 16px;
      margin: 0;
    }
  }
}

.loading-skeleton {
  display: grid;
  gap: 16px;
  
  .skeleton-card {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 20px;
    
    .skeleton-header, .skeleton-body, .skeleton-footer {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      height: 16px;
      margin-bottom: 12px;
      animation: pulse 1.5s ease-in-out infinite;
    }
    
    .skeleton-header {
      height: 20px;
      width: 60%;
    }
    
    .skeleton-body {
      height: 40px;
    }
    
    .skeleton-footer {
      height: 44px;
      margin-bottom: 0;
    }
  }
  
  .skeleton-history-item {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    height: 60px;
    margin-bottom: 12px;
    animation: pulse 1.5s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.6; }
}

.memberships-list {
  display: grid;
  gap: 16px;
  margin-bottom: 32px;
}

.membership-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  position: relative;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &.featured {
    box-shadow: 0 20px 40px rgba(102, 126, 234, 0.2);
  }
  
  .featured-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    background: linear-gradient(135deg, #ffd700, #ffb347);
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .membership-header {
    text-align: center;
    margin-bottom: 20px;
    
    .membership-icon {
      width: 60px;
      height: 60px;
      margin: 0 auto 12px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        font-size: 24px;
        color: white;
      }
    }
    
    h3 {
      color: #333;
      font-size: 20px;
      font-weight: bold;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #666;
      font-size: 14px;
      margin: 0;
    }
  }
  
  .membership-price {
    text-align: center;
    margin-bottom: 20px;
    
    .price-main {
      color: #333;
      font-size: 32px;
      font-weight: bold;
      line-height: 1;
    }
    
    .price-period {
      color: #666;
      font-size: 14px;
      margin-top: 4px;
    }
  }
  
  .membership-features {
    margin-bottom: 24px;
    
    .feature-item {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
      
      i {
        color: #10b981;
        font-size: 14px;
        width: 16px;
      }
      
      span {
        color: #333;
        font-size: 14px;
      }
    }
  }
  
  .purchase-btn {
    width: 100%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 16px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 25px rgba(102, 126, 234, 0.4);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
}

.packages-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 32px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.package-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
  
  .package-header {
    text-align: center;
    margin-bottom: 16px;
    
    .package-icon {
      width: 48px;
      height: 48px;
      margin: 0 auto 12px;
      background: linear-gradient(135deg, #10b981, #059669);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        font-size: 20px;
        color: white;
      }
    }
    
    h3 {
      color: #333;
      font-size: 16px;
      font-weight: bold;
      margin: 0 0 4px 0;
    }
    
    p {
      color: #666;
      font-size: 12px;
      margin: 0;
    }
  }
  
  .package-amount {
    text-align: center;
    margin-bottom: 12px;
    
    .amount-main {
      color: #10b981;
      font-size: 24px;
      font-weight: bold;
      line-height: 1;
    }
    
    .amount-unit {
      color: #666;
      font-size: 12px;
      margin-top: 4px;
    }
  }
  
  .package-price {
    text-align: center;
    margin-bottom: 16px;
    
    .price-main {
      color: #333;
      font-size: 20px;
      font-weight: bold;
      line-height: 1;
    }
    
    .price-per-thousand {
      color: #666;
      font-size: 10px;
      margin-top: 2px;
    }
  }
  
  .purchase-btn {
    width: 100%;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    border: none;
    padding: 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.3s ease;
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
}

.purchase-history {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  .section-header {
    margin-bottom: 16px;
    
    h3 {
      color: #333;
      font-size: 18px;
      font-weight: bold;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      
      i {
        color: #666;
      }
    }
  }
}

.history-list {
  .history-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid #e9ecef;
    
    &:last-child {
      border-bottom: none;
    }
    
    .history-info {
      flex: 1;
      
      .history-name {
        color: #333;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
        
        i {
          color: #667eea;
          font-size: 14px;
        }
      }
      
      .history-date {
        color: #666;
        font-size: 14px;
      }
    }
    
    .history-details {
      text-align: right;
      
      .history-price {
        color: #333;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 4px;
      }
      
      .history-expiry {
        color: #666;
        font-size: 12px;
      }
    }
  }
}

.empty-history {
  text-align: center;
  padding: 40px 20px;
  
  .empty-icon {
    font-size: 48px;
    color: #ccc;
    margin-bottom: 16px;
  }
  
  p {
    color: #666;
    font-size: 16px;
    margin: 0;
  }
}

// 弹窗样式
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  
  h3 {
    color: #333;
    font-size: 20px;
    margin: 0;
    flex: 1;
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f8f9fa;
    border-radius: 50%;
    color: #666;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #e9ecef;
    }
  }
}

.modal-body {
  padding: 20px;
  
  .purchase-details {
    .item-info {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
      padding: 16px;
      background: #f8f9fa;
      border-radius: 12px;
      
      .item-icon {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        i {
          color: white;
          font-size: 20px;
        }
      }
      
      .item-details {
        flex: 1;
        
        h4 {
          color: #333;
          font-size: 16px;
          font-weight: bold;
          margin: 0 0 4px 0;
        }
        
        p {
          color: #666;
          font-size: 14px;
          margin: 0;
        }
      }
    }
    
    .purchase-summary {
      .summary-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #e9ecef;
        
        &:last-child {
          border-bottom: none;
          font-weight: bold;
          
          .price {
            color: #667eea;
            font-size: 18px;
          }
        }
        
        span:first-child {
          color: #666;
        }
        
        span:last-child {
          color: #333;
          font-weight: 500;
        }
      }
    }
  }
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e9ecef;
  
  button {
    flex: 1;
    height: 48px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
    
    &.cancel-btn {
      background: #f8f9fa;
      color: #666;
      border: 2px solid #e9ecef;
      
      &:hover {
        background: #e9ecef;
      }
    }
    
    &.confirm-btn {
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
      
      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 25px rgba(102, 126, 234, 0.4);
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
    }
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .main-content {
    padding: 16px 12px;
  }
  
  .membership-card, .purchase-history {
    padding: 16px;
  }
  
  .package-card {
    padding: 16px;
  }
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tab-content {
  animation: fadeInUp 0.5s ease-out;
}

.membership-card, .package-card {
  animation: fadeInUp 0.6s ease-out;
}

.purchase-history {
  animation: fadeInUp 0.7s ease-out;
}

// 主题切换按钮样式
.theme-toggle {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  i {
    font-size: 16px;
    color: white;
    transition: all 0.3s ease;
  }
}

// 暗色主题样式
.mobile-package-center.dark-theme {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  background-attachment: fixed; /* 确保背景固定 */
}

/* 为暗色主题确保背景色覆盖整个页面高度 */
.mobile-package-center.dark-theme::before {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
}

.mobile-package-center.dark-theme .mobile-header {
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h1 {
    color: #ffffff;
  }

  .back-btn {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .theme-toggle {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.mobile-package-center.dark-theme {
  .main-content {
    .tab-switcher {
      .tab-btn {
        background: rgba(30, 30, 50, 0.6);
        color: rgba(255, 255, 255, 0.7);
        border: 1px solid rgba(255, 255, 255, 0.1);

        &:hover {
          background: rgba(45, 55, 72, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        &.active {
          background: linear-gradient(135deg, #2d3748, #4a5568);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
      }
    }

    .tab-content {
      background: rgba(30, 30, 50, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.1);

      h2 {
        color: #ffffff;
      }

      p {
        color: #ffffff;
      }
    }

    .membership-card, .package-card {
      background: rgba(30, 30, 50, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        background: rgba(45, 55, 72, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.2);
        transform: translateY(-2px);
      }

      &.selected {
        background: rgba(45, 55, 72, 0.9);
        border: 2px solid #4a90e2;
      }

      .card-header h3 {
        color: #ffffff;
      }

      .card-body {
        .features li {
          color: #ffffff;

          &::before {
            color: #4a90e2;
          }
        }

        .price {
          color: #ffffff;

          .price-amount {
            color: #4a90e2;
          }

          .price-unit {
            color: #ffffff;
          }
        }
      }

      .card-footer {
        .purchase-btn {
          background: linear-gradient(135deg, #2d3748, #4a5568);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #e2e8f0;

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #4a5568, #2d3748);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
          }

          &:disabled {
            background: rgba(30, 30, 50, 0.5);
            color: rgba(255, 255, 255, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
        }
      }
    }

    .purchase-history {
      background: rgba(30, 30, 50, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);

      h3 {
        color: #ffffff;
      }

      .history-item {
        background: rgba(45, 55, 72, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.1);

        &:hover {
          background: rgba(45, 55, 72, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .item-info h4 {
          color: #ffffff;
        }

        .item-info p {
          color: #ffffff;
        }

        .item-status {
          &.completed {
            background: rgba(72, 187, 120, 0.2);
            color: #68d391;
          }

          &.pending {
            background: rgba(236, 201, 75, 0.2);
            color: #f6e05e;
          }

          &.failed {
            background: rgba(245, 101, 101, 0.2);
            color: #fc8181;
          }
        }
      }
    }
  }

  // 弹窗样式
  .confirm-modal {
    .modal-content {
      background: rgba(30, 30, 50, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.1);

      .modal-header {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        h3 {
          color: #ffffff;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.2);

          &:hover {
            background: rgba(255, 255, 255, 0.2);
          }
        }
      }

      .modal-body {
        p {
          color: #ffffff;
        }
      }

      .modal-actions {
        .confirm-btn, .cancel-btn {
          background: linear-gradient(135deg, #2d3748, #4a5568);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #e2e8f0;

          &:hover {
            background: linear-gradient(135deg, #4a5568, #2d3748);
            transform: translateY(-2px);
          }
        }

        .cancel-btn {
          background: rgba(255, 255, 255, 0.1);
          
          &:hover {
            background: rgba(255, 255, 255, 0.2);
          }
        }
      }
    }
  }

  // 覆盖所有文字颜色为白色
  h1, h2, h3, h4, h5, h6,
  p, span, div, li, label,
  .card-header h3, .card-body .features li,
  .price, .price-unit, .tab-btn,
  .history-item h4, .history-item p,
  .item-info h4, .item-info p,
  .modal-body p, .form-label {
    color: #ffffff !important;
  }

    // 特殊颜色保持不变
    .status.completed { color: #68d391 !important; }
    .status.pending { color: #f6e05e !important; }
    .status.failed { color: #fc8181 !important; }
    .price-amount { color: #4a90e2 !important; }
    .features li::before { color: #4a90e2 !important; }
}
</style>
